/* eslint-disable no-empty-function */

/**
 * Inner drag class responsible for assigning methods per direction
 */
class RootDrag {

  /**
   * @param {Number} - pixel step size for movement to be considered direction
   */
  constructor({ offset }) {
    this.offset = offset
    this.previous = { x: null, y: null }
  }

  /**
   * Sets initial position on touch start
   *
   * @param {Object} event - event object
   */
  init(event) {
    if (!this.previous.x && !this.previous.y) {
      this.updatePrevious(event)
    }
  }

  /**
   * Set the position on touch moved
   *
   * @param {Object} event - event object
   */
  updatePrevious(event) {
    this.previous.x = event.changedTouches[0].screenX
    this.previous.y = event.changedTouches[0].screenY
  }

  /**
   * Calculates movement direction and calls assigned method
   *
   * @param {Object} event - event object
   */
  handleGesture(event, {
    onLeft = () => { },
    onRight = () => { },
    onUp = () => { },
    onDown = () => { },
  }) {
    const screenX = event.changedTouches[0].screenX
    const screenY = event.changedTouches[0].screenY

    if (this.previous.x + this.offset < screenX) {
      // console.log('right')
      this.updatePrevious(event)

      onRight()
      return
    }

    if (this.previous.x - this.offset > screenX) {
      // console.log('left')
      this.updatePrevious(event)

      onLeft()
      return
    }

    if (this.previous.y + this.offset < screenY) {
      // console.log('down')
      this.updatePrevious(event)

      onDown()
      return
    }

    if (this.previous.y - this.offset > screenY) {
      // console.log('up')
      this.updatePrevious(event)

      onUp()
    }
  }

  /**
   * Reset values
   */
  reset() {
    this.previous = { x: null, y: null }
  }
}

/**
 * Main drag interface to be used in components
 */
export class Drag {
  constructor({ offset }) {
    this.root = new RootDrag({ offset })
    this.pageWidth = window.innerWidth || document.body.clientWidth
    this.threshold = Math.max(1, Math.floor(0.01 * this.pageWidth))
    this.touchstart = { x: 0, y: 0 }
    this.touchend = { x: 0, y: 0 }
    this.limit = Math.tan(45 * 1.5 / 180 * Math.PI)
    this.dragShouldClose = false
    this.movement = 0
  }

  /**
   * Initiates touch events
   *
   * @param {Object} event - event object
   */
  touchStart(event) {
    this.root.init(event)
    this.touchstart.x = event.changedTouches[0].screenX
    this.touchstart.y = event.changedTouches[0].screenY
    this.previousClientY = this.getClientY(event)
  }

  /**
   * Used for handling touch move
   *
   * @param {Object} event - event object
   */
  touchMove(event, {
    onLeft = () => { },
    onRight = () => { },
    onUp = () => { },
    onDown = () => { },
  }) {
    this.touchend.x = event.changedTouches[0].screenX
    this.touchend.y = event.changedTouches[0].screenY

    this.handleGesture(event, { onLeft, onRight, onUp, onDown })
  }

  /**
   * Checks movement is higher than assigned offset and passes value to
   * gesture handlers
   *
   * @param {Object} event - event object
   */
  handleGesture(event, { onLeft, onRight, onUp, onDown }) {
    const touchX = this.touchend.x - this.touchstart.x
    const touchY = this.touchend.y - this.touchstart.y

    if (Math.abs(touchX) > this.threshold || Math.abs(touchY) > this.threshold) {

      this.root.handleGesture(event, {
        onUp: () => onUp(event, touchY),
        onRight: () => onRight(event, touchX),
        onDown: () => onDown(event, touchY),
        onLeft: () => onLeft(event, touchX),
      })
    }
  }

  /**
   * Used via outer method for dragged object - terminating it
   *
   * @param {Boolean} state - will the dragged object close
   */
  setDragShouldClose(state) {
    this.dragShouldClose = state
  }

  /**
   * Reset values
   */
  touchEnd() {
    this.movement = 0
    this.root.reset()
    this.touchstart = { x: 0, y: 0 }
    this.touchend = { x: 0, y: 0 }
  }

  /**
   * Get client Y position from event.
   * @param {Object} event - Touch/mouse event.
   * @returns {Number}
   */
  getClientY(event) {
    return event.type.includes('mouse')
      ? event.clientY
      : event.changedTouches[0].clientY
  }

  /**
   * Calculate move position for each direction
   * @param {*}
   * @returns {Number}
   */
  upMoveStep(event) {
    const newClientY = this.getClientY(event)
    let topMovement = newClientY - this.previousClientY

    /**
     * Check to see if movement is upward.
     * - If upward then reduce the movement distance, creating drag.
     * - Mouse events don't use drag to avoid issues with content selction.
     */
    if (Math.sign(topMovement) === -1) {
      const dragFactor = event.type.includes('mouse') ? 1 : 3
      topMovement = (newClientY - this.previousClientY) / dragFactor
    }

    this.movement = topMovement

    return topMovement
  }
}
